export interface ArtilleryGun {
  index: number;
  x: number;
  y: number;
  crew_x: number;
  crew_y: number;
  crew_alive: number;
  crew_max: number;
  horses_alive: number;
  horses_max: number;
  reload_progress: number;
  ready: boolean;
  blocked_reason: string | null;
}

export interface BatteryArtillery {
  deployment: 'limbered' | 'limbering' | 'deployed' | 'unlimbering';
  transition_ticks: number;
  ammunition: 'round_shot' | 'canister' | 'shell';
  fire_mode: 'direct' | 'indirect';
  supported_modes: string[];
  supported_ammunition: string[];
  can_limber: boolean;
  guns: ArtilleryGun[];
}

export interface ProjectileArtillery {
  height_m: number;
  ground_height_m: number;
  stopped: boolean;
  fuse_ticks: number | null;
}

export type ArtilleryEvent = {army_index: number; tick: number} & (
  | {type: 'artillery_explosion'; projectile_id: string; x: number; y: number;
      height_m: number; radius_m: number; source_unit_id: string}
  | {type: 'artillery_fired'; unit_id: string; gun_index: number;
      ammunition: BatteryArtillery['ammunition']; samples: number}
  | {type: 'artillery_rejected'; unit_id: string; reason: string}
);

const record = (value: unknown): value is Record<string, any> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const count = (value: unknown): value is number => finite(value) && Number.isInteger(value) && value >= 0;
const strings = (value: unknown): value is string[] => Array.isArray(value) && value.every(item => typeof item === 'string');

function decode(raw: unknown): Record<string, any> | undefined {
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return record(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

export function artilleryField(row: Record<string, any>): unknown {
  return row.artillery ?? row.artilleryJson ?? row.artillery_json;
}

export function parseBatteryArtillery(raw: unknown): BatteryArtillery | undefined {
  const value = decode(raw);
  if (!value || !['limbered', 'limbering', 'deployed', 'unlimbering'].includes(value.deployment)
    || !count(value.transition_ticks) || !['round_shot', 'canister', 'shell'].includes(value.ammunition)
    || !['direct', 'indirect'].includes(value.fire_mode) || !strings(value.supported_modes)
    || !strings(value.supported_ammunition) || typeof value.can_limber !== 'boolean'
    || !Array.isArray(value.guns)) return undefined;
  const guns: ArtilleryGun[] = [];
  for (const gun of value.guns) {
    if (!record(gun) || !count(gun.index)
      || !['x', 'y', 'crew_x', 'crew_y', 'reload_progress'].every(key => finite(gun[key]))
      || !['crew_alive', 'crew_max', 'horses_alive', 'horses_max'].every(key => count(gun[key]))
      || gun.crew_alive > gun.crew_max || gun.horses_alive > gun.horses_max
      || gun.reload_progress < 0 || gun.reload_progress > 1 || typeof gun.ready !== 'boolean'
      || !(gun.blocked_reason === null || typeof gun.blocked_reason === 'string')) return undefined;
    guns.push({
      index: gun.index, x: gun.x, y: gun.y, crew_x: gun.crew_x, crew_y: gun.crew_y,
      crew_alive: gun.crew_alive, crew_max: gun.crew_max,
      horses_alive: gun.horses_alive, horses_max: gun.horses_max,
      reload_progress: gun.reload_progress, ready: gun.ready, blocked_reason: gun.blocked_reason,
    });
  }
  if (new Set(guns.map(gun => gun.index)).size !== guns.length) return undefined;
  return {
    deployment: value.deployment, transition_ticks: value.transition_ticks,
    ammunition: value.ammunition, fire_mode: value.fire_mode,
    supported_modes: [...value.supported_modes], supported_ammunition: [...value.supported_ammunition],
    can_limber: value.can_limber, guns,
  };
}

export function parseProjectileArtillery(raw: unknown): ProjectileArtillery | undefined {
  const value = decode(raw);
  if (!value || !finite(value.height_m) || !finite(value.ground_height_m)
    || typeof value.stopped !== 'boolean' || !(value.fuse_ticks === null || count(value.fuse_ticks))) return undefined;
  return {height_m: value.height_m, ground_height_m: value.ground_height_m,
    stopped: value.stopped, fuse_ticks: value.fuse_ticks};
}

export function parseArtilleryEvent(raw: unknown, armyIndex: unknown): ArtilleryEvent | undefined {
  const integer = (value: unknown): value is number => count(value) && Number.isSafeInteger(value);
  const id = (value: unknown): value is string => typeof value === 'string' && value.length > 0 && value.length <= 1024;
  if (!integer(armyIndex) || !record(raw) || raw.army_index !== armyIndex || !integer(raw.tick)) return undefined;
  const context = {army_index: armyIndex, tick: raw.tick};
  switch (raw.type) {
    case 'artillery_explosion':
      if (!id(raw.projectile_id) || !id(raw.source_unit_id)
        || !['x', 'y', 'height_m', 'radius_m'].every(key => finite(raw[key])) || raw.radius_m < 0) return undefined;
      return {type: 'artillery_explosion', ...context, projectile_id: raw.projectile_id,
        x: raw.x, y: raw.y, height_m: raw.height_m, radius_m: raw.radius_m, source_unit_id: raw.source_unit_id};
    case 'artillery_fired':
      if (!id(raw.unit_id) || !integer(raw.gun_index) || !integer(raw.samples)
        || !['round_shot', 'canister', 'shell'].includes(raw.ammunition)) return undefined;
      return {type: 'artillery_fired', ...context, unit_id: raw.unit_id, gun_index: raw.gun_index,
        ammunition: raw.ammunition, samples: raw.samples};
    case 'artillery_rejected':
      if (!id(raw.unit_id) || typeof raw.reason !== 'string' || !raw.reason.length || raw.reason.length > 4096) return undefined;
      return {type: 'artillery_rejected', ...context, unit_id: raw.unit_id, reason: raw.reason};
    default:
      return undefined;
  }
}
